import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithRedirect,
  getRedirectResult,
  signInWithPopup,
  GoogleAuthProvider,
} from './firebaseAuth';
import AuthLayout from '../layouts/AuthLayout';
import { Alert, Button, InputField, Tab, TabList, TextInput } from '../components/ui';
import { typography } from '../design-system/tokens';

const USE_EMULATOR = process.env.REACT_APP_USE_EMULATORS === 'true';

const MODE_SIGNUP = 'signup';
const MODE_LOGIN = 'login';
const MODE_FORGOT = 'forgot';

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'login' ? MODE_LOGIN : MODE_SIGNUP;
  const [mode, setMode] = useState(initialMode);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const isSignUp = mode === MODE_SIGNUP;
  const isForgot = mode === MODE_FORGOT;

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setResetEmailSent(false);
  };

  useEffect(() => {
    let cancelled = false;
    getRedirectResult(auth).then((result) => {
      if (cancelled || !result) return;
      navigate('/create-profile', { replace: true });
    }).catch((err) => {
      if (!cancelled) setError(err.message || 'Sign-in failed.');
    });
    return () => { cancelled = true; };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const cleanEmail = (email || '').trim().toLowerCase();
    if (!cleanEmail) {
      setError('Enter your email.');
      setLoading(false);
      return;
    }

    try {
      if (isSignUp) {
        if (!password || password.length < 6) {
          setError('Password must be at least 6 characters.');
          setLoading(false);
          return;
        }
        await createUserWithEmailAndPassword(auth, cleanEmail, password);
      } else {
        if (!password) {
          setError('Enter your password.');
          setLoading(false);
          return;
        }
        await signInWithEmailAndPassword(auth, cleanEmail, password);
      }
      navigate('/create-profile', { replace: true });
    } catch (err) {
      const code = err.code || '';
      const msg =
        code === 'auth/email-already-in-use'
          ? 'That email is already registered. Log in instead.'
          : code === 'auth/invalid-email'
            ? 'Please enter a valid email address.'
            : code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found'
              ? 'Wrong email or password.'
              : code === 'auth/too-many-requests'
                ? 'Too many attempts. Try again later.'
                : err.message || 'Something went wrong.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const cleanEmail = (email || '').trim().toLowerCase();
    if (!cleanEmail) {
      setError('Enter your email to receive a reset link.');
      setLoading(false);
      return;
    }
    try {
      await sendPasswordResetEmail(auth, cleanEmail);
      setResetEmailSent(true);
    } catch (err) {
      const code = err?.code || '';
      const msg =
        code === 'auth/invalid-email'
          ? 'Invalid email. Please try again.'
          : code === 'auth/user-not-found'
            ? 'No account found for that email.'
            : code === 'auth/too-many-requests'
              ? 'Too many attempts. Try again later.'
              : err?.message || 'Could not send reset email.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      if (USE_EMULATOR) {
        await signInWithPopup(auth, new GoogleAuthProvider());
        navigate('/create-profile', { replace: true });
      } else {
        signInWithRedirect(auth, new GoogleAuthProvider());
      }
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        setError('');
      } else {
        setError(err?.message || 'Google sign-in failed.');
      }
    } finally {
      if (USE_EMULATOR) setLoading(false);
    }
  };

  return (
    <AuthLayout>
      {!isForgot && (
        <TabList className="mb-8">
          <Tab active={isSignUp} onClick={() => switchMode(MODE_SIGNUP)}>Sign up</Tab>
          <Tab active={mode === MODE_LOGIN} onClick={() => switchMode(MODE_LOGIN)}>Log in</Tab>
        </TabList>
      )}

      <h1 className={typography.heading}>
        {isForgot
          ? 'Reset password'
          : isSignUp
            ? 'Create your account'
            : 'Welcome back'}
      </h1>
      <p className={`${typography.body} mb-6`}>
        {isForgot
          ? 'Enter your email and we will send you a password reset link.'
          : isSignUp
            ? 'Enter your email and choose a password (min 6 characters).'
            : 'Enter your email and password.'}
      </p>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      <form
        onSubmit={isForgot ? handleForgotPassword : handleSubmit}
        className="space-y-4"
      >
        {isForgot && resetEmailSent ? null : (
          <>
            <InputField label="Email">
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
              />
            </InputField>
            {!isForgot && (
              <InputField label="Password">
                <TextInput
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={isSignUp ? 6 : undefined}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  placeholder={isSignUp ? 'At least 6 characters' : 'Your password'}
                />
              </InputField>
            )}
            {mode === MODE_LOGIN && !isForgot && (
              <p className="text-right -mt-1">
                <Button variant="link" type="button" onClick={() => switchMode(MODE_FORGOT)}>
                  Forgot password?
                </Button>
              </p>
            )}
          </>
        )}

        {!(isForgot && resetEmailSent) && (
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={loading}
            className="mt-6"
          >
            {loading
              ? 'Please wait…'
              : isForgot
                ? 'Send reset link'
                : isSignUp
                  ? 'Create account'
                  : 'Log in'}
          </Button>
        )}
      </form>

      {isForgot && !resetEmailSent && (
        <p className="mt-4 text-center">
          <Button variant="link" type="button" onClick={() => switchMode(MODE_LOGIN)}>
            ← Back to log in
          </Button>
        </p>
      )}

      {isForgot && resetEmailSent && (
        <Alert tone="success" className="mt-4 text-center">
          Check your email for a password reset link.
        </Alert>
      )}

      {!isForgot && (
        <>
          <div className="relative my-6">
            <span className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-white/10" />
            </span>
            <span className="relative flex justify-center text-xs font-bold uppercase tracking-widest text-gray-500">
              Or
            </span>
          </div>
          <Button variant="ghost" fullWidth disabled={loading} onClick={handleGoogleSignIn}>
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" aria-hidden>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </Button>
        </>
      )}
    </AuthLayout>
  );
};

export default AuthPage;
